import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useDcrContext } from '../_context/DcrContext';

export const BaseSections = {
	Name: 'name',
	Ids: 'ids',
	Contact: 'contact',
	Hierarchy: 'hierarchy',
	Affiliations: 'affiliations',
	Addresses: 'addresses',
	Licenses: 'licenses'
} as const;

export const HcpSections = {
	...BaseSections,
	Professional: 'professional',
	Demographics: 'demographics'
} as const;

export const HcoSections = {
	...BaseSections,
	Organizational: 'organizational'
} as const;

export const AllSections = {
	...HcpSections,
	...HcoSections
} as const;

export type HcpSection = (typeof HcpSections)[keyof typeof HcpSections];
export type HcoSection = (typeof HcoSections)[keyof typeof HcoSections];
export type AllSection = (typeof AllSections)[keyof typeof AllSections];

export type Field = {
	label: string;
	section: AllSection | AllSection[];
	hidden?: boolean;
};

export type Section = {
	label: string;
	fields: string[];
	hidden?: boolean;
};

export const useDcrFields = () => {
	const { t } = useTranslation();

	const {
		entityCountryId,
		picklists: { academicStatusOptions }
	} = useDcrContext();

	const addressFields: Record<string, Field> = useMemo(
		() => ({
			addresses: { label: t('Address'), section: BaseSections.Addresses },
			primaryCda_V: {
				label: t('Primary'),
				section: BaseSections.Addresses,
				hidden: true // Note (Ben): This isn't actually hidden, it's just part of the card component.
			},
			streetAddress1Cda_V: {
				label: t('Street Address 1'),
				section: BaseSections.Addresses
			},
			streetAddress2Cda_V: {
				label: t('Street Address 2'),
				section: BaseSections.Addresses
			},
			cityCda_V: { label: t('City'), section: BaseSections.Addresses },
			stateCda_V: { label: t('State'), section: BaseSections.Addresses },
			postalCodeCda_V: {
				label: t('Postal Code'),
				section: BaseSections.Addresses
			},
			postalCodeFullUs_V: {
				label: t('Full Postal Code US'),
				section: BaseSections.Addresses
			},
			countryCda_V: { label: t('Country'), section: BaseSections.Addresses },
			coordinates_V: {
				label: t('Coordinates'),
				section: BaseSections.Addresses,
				hidden: true
			},
			subStateAdminUnit_V: {
				label: t('Region/County'),
				section: BaseSections.Addresses
			},
			cbsa_V: {
				label: t('Metropolitan (CBSA)'),
				section: BaseSections.Addresses,
				hidden: true
			},
			phoneCda_V: {
				label: t('Phone'),
				section: BaseSections.Addresses
			},
			faxCda_V: {
				label: t('Fax'),
				section: BaseSections.Addresses
			}
		}),
		[t]
	);

	const affiliationFields: Record<string, Field> = useMemo(
		() => ({
			parentHcos_V: {
				label: t('Affiliation'),
				section: BaseSections.Affiliations,
				hidden: true
			},
			parentHcoVid_V: {
				label: t('ID'),
				section: BaseSections.Affiliations,
				hidden: true
			},
			parentHcoVid: {
				label: t('ID'),
				section: BaseSections.Affiliations,
				hidden: true
			},
			parentHcoStatus_V: {
				label: t('Status'),
				section: BaseSections.Affiliations,
				hidden: true // Note (Ben): This isn't actually hidden, it's just part of the card component.
			},
			hcoStatus: {
				label: t('Status'),
				section: BaseSections.Affiliations,
				hidden: true
			},
			isPrimaryRelationship_V: {
				label: t('Primary'),
				section: BaseSections.Affiliations,
				hidden: true // Note (Ben): This isn't actually hidden, it's just part of the card component.
			},
			hcoNameCda_V: { label: t('Name'), section: BaseSections.Affiliations },
			hcoType: {
				label: t('Global HCO Type'),
				section: BaseSections.Affiliations
			},
			location: {
				// locationRole_V
				label: t('Location'),
				section: BaseSections.Affiliations
			},
			jobTitle_V: {
				label: t('Job Title'),
				section: BaseSections.Affiliations
			},
			typeOfRelationship_VId: {
				label: t('Type of Relationship'),
				section: BaseSections.Affiliations
			},
			principalAffiliationName_V: {
				label: t('Principle Name'),
				section: BaseSections.Affiliations
			}
		}),
		[t]
	);

	const licenseFields: Record<string, Field> = useMemo(
		() => ({
			licenses: { label: t('Licenses'), section: BaseSections.Licenses },
			licenseNumber_V: {
				label: t('License ID'),
				section: BaseSections.Licenses
			},
			licenseDegree_V: {
				label: t('License Degree'),
				section: BaseSections.Licenses
			},
			typeValue_VId: {
				// typeValue_V
				label: t('Licensing Authority'),
				section: BaseSections.Licenses
			},
			expirationDate_V: {
				label: t('Expiration Date'),
				section: BaseSections.Licenses
			},
			licenseStatus_V: {
				label: t('License Status'),
				section: BaseSections.Licenses
			},
			gracePeriod_V: {
				label: t('Grace Expiry'),
				section: BaseSections.Licenses,
				hidden: true
			},
			rxaEligible_V: {
				label: t('Sample Eligibility'),
				section: BaseSections.Licenses,
				hidden: true
			},
			type_V: {
				label: t('License Type'),
				section: BaseSections.Licenses,
				hidden: true
			},
			licenseSubtype_V: {
				label: t('License SubType'),
				section: BaseSections.Licenses,
				hidden: true
			},
			body_V: {
				label: t('Licensing Body'),
				section: BaseSections.Licenses,
				hidden: true
			},
			licenseCertification_V: {
				label: t('License Certification'),
				section: BaseSections.Licenses,
				hidden: true
			},
			bestStateLicense_V: {
				label: t('Best State License'),
				section: BaseSections.Licenses,
				hidden: true
			},
			licenseEligibility_V: {
				label: t('Prescriptive Authority'),
				section: BaseSections.Licenses,
				hidden: true
			},
			licenseStatusCondition_V: {
				label: t('License Status Condition'),
				section: BaseSections.Licenses,
				hidden: true
			},
			comprehensivePrescriptiveAuthority_V: {
				label: t('Comprehensive Prescriptive Authority'),
				section: BaseSections.Licenses,
				hidden: true
			},
			comprehensiveSampleEligibility_V: {
				label: t('Comprehensive Sample Eligibility'),
				section: BaseSections.Licenses,
				hidden: true
			},
			collabAgreeRequired_V: {
				label: t('Collaborative Agreement Required'),
				section: BaseSections.Licenses,
				hidden: true
			},
			ctrlSubstancePrescriptive_V: {
				label: t('Controlled Substances Prescriptive Authority'),
				section: BaseSections.Licenses,
				hidden: true
			},
			ctrlSubstanceSample_V: {
				label: t('Controlled Substances Sample Eligibility'),
				section: BaseSections.Licenses,
				hidden: true
			},
			deaBusinessActivityCode_V: {
				label: t('DEA Activity Code'),
				section: BaseSections.Licenses,
				hidden: true
			},
			deaPaymentIndicator_V: {
				label: t('DEA Payment Indicator'),
				section: BaseSections.Licenses,
				hidden: true
			},
			drugSchedule_V: {
				label: t('Drug Schedule Details'),
				section: BaseSections.Licenses,
				hidden: true
			}
		}),
		[t]
	);

	// TODO: type string with field names
	const hcpFields: Record<string, Field> = useMemo(
		() => ({
			// Affiliations
			...affiliationFields,

			// Addresses
			...addressFields,

			// Licenses
			...licenseFields,

			// Name
			firstNameCda_V: { label: t('First Name'), section: HcpSections.Name },
			middleNameCda_V: { label: t('Middle Name'), section: HcpSections.Name },
			lastNameCda_V: { label: t('Last Name'), section: HcpSections.Name },
			prefixCda_V: { label: t('Prefix'), section: HcpSections.Name },
			suffixCda_V: { label: t('Suffix'), section: HcpSections.Name },
			internationalName_V: {
				label: t('International Name'),
				section: HcpSections.Name,
				hidden: true
			},

			// Professional
			hcpType: {
				label: t('Global HCP Type'),
				section: HcpSections.Professional
			},
			hcpTypeLocal_VId: {
				label: t('Local HCP Type'),
				section: HcpSections.Professional
			},
			degrees: {
				label: t('Medical Degrees'),
				section: HcpSections.Professional
			},
			primaryCountry_V: {
				label: t('Country'),
				section: HcpSections.Professional,
				hidden: true
			},
			hcpSpecialties: {
				// spec1Cda_V
				label: t('Global Specialty'),
				section: HcpSections.Professional,
				hidden: true
			},
			allSpecLocal_VId: {
				label: t('Local Specialties'),
				section: HcpSections.Professional
			},
			statusCda_V: { label: t('Status'), section: HcpSections.Professional },
			hcpStatus_V: {
				label: t('Status Reason'),
				section: HcpSections.Professional,
				hidden: true
			},
			nhidCda_V: {
				label: t('National Healthcare ID'),
				section: BaseSections.Ids
			},
			nhid2_V: {
				label: t('Secondary Healthcare ID'),
				section: BaseSections.Ids
			},
			prescriberCda_V: {
				label: t('Prescriber'),
				section: HcpSections.Professional,
				hidden: true
			},
			telehealth_V: {
				label: t('Telehealth'),
				section: HcpSections.Professional,
				hidden: true
			},
			inCurrentPecos_V: {
				label: t('In Current PECOS'),
				section: HcpSections.Professional,
				hidden: true
			},

			// Demographics
			languageCda_V: {
				label: t('Language'),
				section: HcpSections.Demographics,
				hidden: true
			},
			gender_V: { label: t('Gender'), section: HcpSections.Demographics },
			birthYear_V: {
				label: t('Year of Birth'),
				section: HcpSections.Demographics
			},
			dataPrivacyOptOut_V: {
				label: t('OpenData Data Privacy Opt Out'),
				section: HcpSections.Demographics,
				hidden: true
			},
			gradSchool_V: {
				label: t('Graduation School'),
				section: HcpSections.Demographics,
				hidden: true
			},
			gradYear_V: {
				label: t('Graduation Year'),
				section: HcpSections.Demographics,
				hidden: true
			},

			// Contact
			officePhoneCda_V: {
				label: t('Office Phone'),
				section: HcpSections.Contact,
				hidden: true
			},
			phone2_V: {
				label: t('Office Phone 2'),
				section: BaseSections.Addresses,
				hidden: true
			},
			faxCda_V: { label: t('Fax'), section: HcpSections.Contact, hidden: true },
			// Only US HCPs support multiple emails, so only they get the numbered
			// "Email 1" form - non-US HCPs always just get "Email".
			emailCda_V: {
				label:
					entityCountryId === 'us'
						? t('Email {{number}}', { number: 1 })
						: t('Email'),
				section: HcpSections.Contact
			},
			email2_V: {
				label: t('Email {{number}}', { number: 2 }),
				section: HcpSections.Contact
			},
			email3_V: {
				label: t('Email {{number}}', { number: 3 }),
				section: HcpSections.Contact
			},
			email4_V: {
				label: t('Email {{number}}', { number: 4 }),
				section: HcpSections.Contact
			},
			email5_V: {
				label: t('Email {{number}}', { number: 5 }),
				section: HcpSections.Contact
			},
			websiteCda_V: {
				label: t('Website'),
				section: HcpSections.Contact
			},
			website_V: {
				label: t('Website'),
				section: HcpSections.Contact,
				hidden: true
			},

			// Other Attributes
			jobType_V: {
				label: t('Job Type'),
				section: HcpSections.Professional,
				hidden: true
			},
			certifiedTrainer_V: {
				label: t('Certified Trainings'),
				section: HcpSections.Professional,
				hidden: true
			}
		}),
		[t, addressFields, affiliationFields, licenseFields, entityCountryId]
	);

	// Note (Ben): Temporarily Partial while local attrs not supported.
	const hcpSections: Partial<Record<HcpSection, Section>> = useMemo(() => {
		const getFieldsForSection = (section: HcpSection) =>
			Object.entries(hcpFields)
				.filter(([_, value]) =>
					Array.isArray(value.section)
						? value.section.includes(section)
						: value.section === section
				)
				.map(([key]) => key);

		return {
			[HcpSections.Name]: {
				label: t('Name'),
				fields: getFieldsForSection(HcpSections.Name)
			},
			[HcpSections.Professional]: {
				label: t('Professional'),
				fields: getFieldsForSection(HcpSections.Professional)
			},
			[HcpSections.Ids]: {
				label: t('IDs'),
				fields: getFieldsForSection(HcpSections.Ids)
			},
			[HcpSections.Demographics]: {
				label: t('Demographics'),
				fields: getFieldsForSection(HcpSections.Demographics)
			},
			[HcpSections.Contact]: {
				label: t('Contact'),
				fields: getFieldsForSection(HcpSections.Contact)
			},
			[HcpSections.Affiliations]: {
				label: t('Affiliations'),
				fields: getFieldsForSection(HcpSections.Affiliations)
			},
			[HcpSections.Addresses]: {
				label: t('Addresses'),
				fields: getFieldsForSection(HcpSections.Addresses)
			},
			[HcpSections.Licenses]: {
				label: t('Licenses'),
				fields: getFieldsForSection(HcpSections.Licenses)
			}
		};
	}, [t, hcpFields]);

	const hcoFields: Record<string, Field> = useMemo(
		() => ({
			// Affiliations
			...affiliationFields,
			parenthcos: {
				label: t('Affiliation'),
				section: HcoSections.Affiliations,
				hidden: true
			},
			parentHcoVid_V: {
				label: t('ID'),
				section: HcoSections.Affiliations,
				hidden: true
			},

			// Addresses
			...addressFields,
			addresses_V: { label: t('Address'), section: HcoSections.Addresses },

			// Licenses
			// ...licenseFields

			// Name
			hcoNameCda_V: {
				label: t('Name'),
				section: [HcoSections.Name, HcoSections.Affiliations]
			},

			// Organizational
			hcoType: {
				label: t('Global HCO Type'),
				section: [HcoSections.Organizational, HcoSections.Affiliations]
			},
			primaryCountry_V: {
				label: t('Country'),
				section: HcoSections.Organizational,
				hidden: true
			},
			statusCda_V: { label: t('Status'), section: HcoSections.Organizational },
			nhidCda_V: {
				label: t('National Healthcare ID'),
				section: BaseSections.Ids
			},
			businessType_V: {
				label: t('Ownership'),
				section: HcoSections.Organizational,
				hidden: true
			},
			hcoOwnership_VId: {
				label: t('Ownership'),
				section: HcoSections.Organizational
			},
			academicStatus_VId: {
				label: t('Academic Status'),
				section: HcoSections.Organizational,
				hidden: !academicStatusOptions?.length
			},
			countPrescriber_VId: {
				label: t('Number of Affiliated Prescribers'),
				section: HcoSections.Organizational
			},
			countHcp_VId: {
				label: t('Number of Total Affiliated HCPs'),
				section: HcoSections.Organizational
			},
			hcoTypeLocal_VId: {
				label: t('Local HCO Type'),
				section: HcoSections.Organizational
			},
			hcoTaxId_V: { label: t('Tax ID'), section: BaseSections.Ids },
			vaDodAffiliated_V: {
				label: t('Veterans Affairs - Department of Defense'),
				section: HcoSections.Organizational,
				hidden: true
			},

			// Contact
			officePhoneCda_V: {
				label: t('Office Phone'),
				section: HcoSections.Contact,
				hidden: true
			},
			faxCda_V: { label: t('Fax'), section: HcoSections.Contact, hidden: true },
			emailCda_V: { label: t('Email'), section: HcoSections.Contact },
			websiteCda_V: { label: t('Website'), section: HcoSections.Contact },

			allSpecLocal_VId: {
				label: t('Local Specialties'),
				section: HcoSections.Organizational
			},

			// Hierarchy
			typeOfHierarchy_VId: {
				label: t('Type of Hierarchy'),
				section: HcoSections.Hierarchy
			}
		}),
		[t, addressFields, affiliationFields, academicStatusOptions]
	);

	// Note (Ben): Temporarily Partial while local attrs and licenses not supported.
	const hcoSections: Partial<Record<HcoSection, Section>> = useMemo(() => {
		const getFieldsForSection = (section: HcoSection) =>
			Object.entries(hcoFields)
				.filter(([_, value]) =>
					Array.isArray(value.section)
						? value.section.includes(section)
						: value.section === section
				)
				.map(([key]) => key);

		return {
			[HcoSections.Name]: {
				label: t('Name'),
				fields: getFieldsForSection(HcoSections.Name)
			},
			[HcoSections.Organizational]: {
				label: t('Organizational'),
				fields: getFieldsForSection(HcoSections.Organizational)
			},
			[HcoSections.Ids]: {
				label: t('IDs'),
				fields: getFieldsForSection(HcoSections.Ids)
			},
			[HcoSections.Contact]: {
				label: t('Contact'),
				fields: getFieldsForSection(HcoSections.Contact)
			},
			[HcoSections.Affiliations]: {
				label: t('Affiliation'),
				fields: getFieldsForSection(HcoSections.Affiliations)
			},
			[HcoSections.Addresses]: {
				label: t('Address'),
				fields: getFieldsForSection(HcoSections.Addresses)
			}
			// Note (Ben): Not supported in DCR yet
			// [HcoSections.Licenses]: {
			// 	label: t('License'),
			// 	fields: getFieldsForSection(HcoSections.Licenses)
			// }
		};
	}, [t, hcoFields]);

	const allFields: Record<string, string> = useMemo(() => {
		const combinedFields = {
			...hcpFields,
			...hcoFields
		};

		return Object.entries(combinedFields).reduce(
			(acc, [key, value]) => {
				acc[key] = value.label;
				return acc;
			},
			{} as Record<string, string>
		);
	}, [hcpFields, hcoFields]);

	return {
		hcpSections,
		hcpFields,
		hcoSections,
		hcoFields,
		addressFields,
		affiliationFields,
		licenseFields,
		allFields
	};
};
